import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../i18n/LocalizationProvider';
import type { TranslationContent } from '../i18n/translations';
import { applyTestToProfile, createProfile, getStore, revealResult } from '../data/portalStore';
import type { PortalStore } from '../data/portalStore';
import { useAuth } from '../auth/AuthContext';

export default function SubuserPage() {
  const { t } = useTranslation();
  const labels = t<TranslationContent['subuser']>('subuser');
  const { session } = useAuth();
  const [store, setStore] = useState<PortalStore>(() => getStore());
  const [profileName, setProfileName] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    setStore(getStore());
  }, []);

  const subuser = store.subusers.find(item => item.id === session?.userId);
  const profiles = store.profiles.filter(profile => profile.subuserId === subuser?.id);

  const totals = useMemo(() => {
    const revealed = profiles.filter(profile => profile.status === 'revealed').length;
    return { profiles: profiles.length, revealed };
  }, [profiles]);

  const handleCreateProfile = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage('');
    if (!subuser || !profileName.trim()) {
      setMessage(labels.create.error);
      return;
    }
    const result = createProfile(subuser.orgId, subuser.id, profileName.trim());
    setStore(result.store);
    setProfileName('');
    setMessage(labels.create.success);
  };

  const handleApply = (profileId: string) => {
    setMessage('');
    if (!session) return;
    const result = applyTestToProfile(profileId, session.userId);
    if (result.error) {
      setMessage(labels.apply.error);
      return;
    }
    setStore(result.store);
  };

  const handleReveal = (profileId: string) => {
    setMessage('');
    if (!session) return;
    const result = revealResult(profileId, session.userId);
    if (result.error) {
      setMessage(labels.reveal.error);
      return;
    }
    setStore(result.store);
  };

  if (!subuser) {
    return (
      <div className="panel">
        <p className="text-muted">{labels.empty}</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="section-heading">
        <div>
          <span className="badge">{labels.badge}</span>
          <h2>{labels.title}</h2>
          <p className="text-muted">{labels.description}</p>
        </div>
        <div className="metric-card" style={{ margin: 0, padding: '1rem 1.25rem' }}>
          <span>{labels.balance.available}</span>
          <strong>{subuser.allocatedTests}</strong>
        </div>
      </div>

      <div className="metric-grid" style={{ marginBottom: '2rem' }}>
        <div className="metric-card">
          <span>{labels.balance.profiles}</span>
          <strong>{totals.profiles}</strong>
        </div>
        <div className="metric-card">
          <span>{labels.balance.revealed}</span>
          <strong>{totals.revealed}</strong>
        </div>
      </div>

      {message && <p className="text-muted">{message}</p>}

      <form
        className="panel"
        onSubmit={handleCreateProfile}
        style={{ boxShadow: 'none', border: '1px dashed var(--color-border)', marginBottom: '2rem' }}
      >
        <h3>{labels.create.title}</h3>
        <p className="text-muted">{labels.create.description}</p>
        <div className="form-grid two-cols">
          <input
            value={profileName}
            onChange={event => setProfileName(event.target.value)}
            placeholder={labels.create.placeholder}
            required
          />
          <button type="submit">{labels.create.submit}</button>
        </div>
      </form>

      <div className="panel" style={{ boxShadow: 'none' }}>
        <h3>{labels.profiles.title}</h3>
        <table>
          <thead>
            <tr>
              <th>{labels.profiles.table.profile}</th>
              <th>{labels.profiles.table.status}</th>
              <th>{labels.profiles.table.tier}</th>
              <th>{labels.profiles.table.actions}</th>
            </tr>
          </thead>
          <tbody>
            {profiles.map(profile => (
              <tr key={profile.id}>
                <td>{profile.name}</td>
                <td>{labels.profiles.status[profile.status]}</td>
                <td>{profile.status === 'revealed' ? profile.riskTier : labels.profiles.hidden}</td>
                <td>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={profile.status !== 'draft' || subuser.allocatedTests <= 0}
                      onClick={() => handleApply(profile.id)}
                    >
                      {labels.apply.submit}
                    </button>
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={profile.status !== 'applied'}
                      onClick={() => handleReveal(profile.id)}
                    >
                      {labels.reveal.submit}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
